import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ApiError } from "@/services/BaseService";
import { useStartGuestExport, useStartUserExport } from "@/hooks/useDataExport";
import {
  ExportTargetDescriptor,
  ExportTargetType,
  StartExportDto,
} from "@/types/data-export";
import { ExportConfirmBody } from "./ExportConfirmBody";
import { ExportJobStatusView } from "./ExportJobStatusView";

type FlowPhase = "confirm" | "status";

export interface ExportFlow {
  /** Open the confirm dialog for a target. */
  startExport: (descriptor: ExportTargetDescriptor) => void;
  /** Open the status panel for an existing job. */
  viewJob: (jobId: string) => void;
  /** Render once in the host component. */
  dialogs: React.ReactNode;
}

/**
 * Drives the GDPR export UX for a screen: one dialog that starts at a non-
 * destructive confirm step and switches to the live status panel on `202`.
 */
export const useExportFlow = (): ExportFlow => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<FlowPhase>("confirm");
  const [descriptor, setDescriptor] = useState<ExportTargetDescriptor | null>(
    null,
  );
  const [jobId, setJobId] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const guestMutation = useStartGuestExport();
  const userMutation = useStartUserExport();

  const mutationByType = useMemo(
    () => ({
      [ExportTargetType.GUEST]: guestMutation,
      [ExportTargetType.USER]: userMutation,
    }),
    [guestMutation, userMutation],
  );

  const activeMutation = descriptor ? mutationByType[descriptor.type] : null;

  const close = useCallback(() => {
    setOpen(false);
    setDescriptor(null);
    setJobId(null);
    setServerError(null);
    setPhase("confirm");
  }, []);

  const startExport = useCallback((next: ExportTargetDescriptor) => {
    setDescriptor(next);
    setJobId(null);
    setServerError(null);
    setPhase("confirm");
    setOpen(true);
  }, []);

  const viewJob = useCallback((nextJobId: string) => {
    setDescriptor(null);
    setJobId(nextJobId);
    setServerError(null);
    setPhase("status");
    setOpen(true);
  }, []);

  const handleStartError = useCallback(
    (error: unknown, target: ExportTargetDescriptor) => {
      if (error instanceof ApiError) {
        if (error.statusCode === 409) {
          toast.error(
            t("dataExport.job.toast.inFlight", {
              target: t(`dataExport.targetWord.${target.type}`),
            }),
          );
          close();
          return;
        }
        if (error.statusCode === 404) {
          toast.error(t("dataExport.job.toast.notFound"));
          close();
          return;
        }
      }
      setServerError(error instanceof Error ? error.message : String(error));
    },
    [close, t],
  );

  const handleConfirm = useCallback(async () => {
    if (!descriptor) {
      return;
    }
    const mutation = mutationByType[descriptor.type];
    const dto: StartExportDto = {};
    if (descriptor.type === ExportTargetType.GUEST && descriptor.tenantId) {
      dto.tenantId = descriptor.tenantId;
    }

    try {
      const job = await mutation.mutateAsync({ id: descriptor.id, dto });
      toast.success(t("dataExport.job.toast.started"));
      setJobId(job.id);
      setServerError(null);
      setPhase("status");
    } catch (error) {
      handleStartError(error, descriptor);
    }
  }, [descriptor, handleStartError, mutationByType, t]);

  const dialogs = (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          close();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        {phase === "confirm" && descriptor ? (
          <ExportConfirmBody
            descriptor={descriptor}
            isPending={activeMutation?.isPending ?? false}
            serverError={serverError}
            onConfirm={() => void handleConfirm()}
            onCancel={close}
          />
        ) : null}
        {phase === "status" && jobId ? (
          <ExportJobStatusView key={jobId} jobId={jobId} onClose={close} />
        ) : null}
      </DialogContent>
    </Dialog>
  );

  return { startExport, viewJob, dialogs };
};
